import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import "@/i18n/config";

// Components
import { AuthGuard } from "./components/auth/AuthGuard";
import { InstallPrompt } from "./components/InstallPrompt";
import { useServiceWorkerUpdate } from "./hooks/useServiceWorkerUpdate";
import { Loader2 } from "lucide-react";

// Lazy load pages for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const PatientDashboard = lazy(() => import("./pages/patient/Dashboard"));
const PatientCapture = lazy(() => import("./pages/patient/Capture"));
const GuidedCapture = lazy(() => import("./pages/patient/GuidedCapture"));
const PatientHistory = lazy(() => import("./pages/patient/History"));
const PatientMessages = lazy(() => import("./pages/patient/Messages"));
const LinkTreatment = lazy(() => import("./pages/patient/LinkTreatment"));
const OrthodontistDashboard = lazy(() => import("./pages/orthodontist/Dashboard"));
const OrthodontistPatients = lazy(() => import("./pages/orthodontist/Patients"));
const OrthodontistTreatment = lazy(() => import("./pages/orthodontist/Treatment"));
const OrthodontistViewer = lazy(() => import("./pages/orthodontist/Viewer"));
const OrthodontistProfile = lazy(() => import("./pages/orthodontist/Profile"));
const ComparisonViewer = lazy(() => import("./pages/orthodontist/ComparisonViewer"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  useServiceWorkerUpdate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("App mounting, checking session...");
    
    // Check for existing session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Session check complete:", session ? "Logged in" : "Not logged in");
      setSession(session);
      setLoading(false);
    }).catch(error => {
      console.error("Error getting session:", error);
      setLoading(false);
    });

    // Then set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="orthovirtual-theme">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            }>
              <Routes>
            {/* Public routes */}
            <Route path="/" element={session ? <Navigate to="/dashboard" replace /> : <Index />} />
            <Route path="/auth" element={session ? <Navigate to="/dashboard" replace /> : <Auth />} />
            <Route path="/install" element={<Install />} />
            
            {/* Protected Dashboard - redirects based on role */}
            <Route path="/dashboard" element={
              <AuthGuard>
                <DashboardRedirect />
              </AuthGuard>
            } />
            
            {/* Patient routes */}
            <Route path="/patient" element={
              <AuthGuard requiredRole="PATIENT">
                <PatientDashboard />
              </AuthGuard>
            } />
            <Route path="/patient/capture" element={
              <AuthGuard requiredRole="PATIENT">
                <PatientCapture />
              </AuthGuard>
            } />
            <Route path="/patient/guided-capture" element={
              <AuthGuard requiredRole="PATIENT">
                <GuidedCapture />
              </AuthGuard>
            } />
            <Route path="/patient/history" element={
              <AuthGuard requiredRole="PATIENT">
                <PatientHistory />
              </AuthGuard>
            } />
            <Route path="/patient/messages" element={
              <AuthGuard requiredRole="PATIENT">
                <PatientMessages />
              </AuthGuard>
            } />
            <Route path="/patient/link-treatment" element={
              <AuthGuard requiredRole="PATIENT">
                <LinkTreatment />
              </AuthGuard>
            } />
            
            {/* Orthodontist routes */}
            <Route path="/orthodontist" element={
              <AuthGuard requiredRole="ORTHODONTIST">
                <OrthodontistDashboard />
              </AuthGuard>
            } />
            <Route path="/orthodontist/profile" element={
              <AuthGuard requiredRole="ORTHODONTIST">
                <OrthodontistProfile />
              </AuthGuard>
            } />
            <Route path="/orthodontist/patients" element={
              <AuthGuard requiredRole="ORTHODONTIST">
                <OrthodontistPatients />
              </AuthGuard>
            } />
            <Route path="/orthodontist/treatment/:id" element={
              <AuthGuard requiredRole="ORTHODONTIST">
                <OrthodontistTreatment />
              </AuthGuard>
            } />
            <Route path="/orthodontist/viewer/:submissionId" element={
              <AuthGuard requiredRole="ORTHODONTIST">
                <OrthodontistViewer />
              </AuthGuard>
            } />
            <Route path="/orthodontist/comparison-viewer/:submissionId" element={
              <AuthGuard requiredRole="ORTHODONTIST">
                <ComparisonViewer />
              </AuthGuard>
            } />
            
            {/* Catch all */}
            <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <InstallPrompt />
          </TooltipProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

// Helper component to redirect based on user role
function DashboardRedirect() {
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUserRole() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('user_id', user.id)
          .single();
        
        setRole(profile?.role || null);
      }
    }
    
    fetchUserRole();
  }, []);

  if (!role) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (role === 'ORTHODONTIST') {
    return <Navigate to="/orthodontist" replace />;
  }
  
  return <Navigate to="/patient" replace />;
}

export default App;
